import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  createEquipmentSchema,
  type CreateEquipmentInput,
  type EquipmentFormOptions,
} from "@crewledger/shared/schemas";
import { PageHeader } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  EQUIPMENT_STATUS_LABELS,
  EQUIPMENT_TYPE_LABELS,
  createEquipment,
  fetchEquipmentItem,
  fetchEquipmentOptions,
  updateEquipment,
} from "./api";
import { CERTIFICATION_TYPE_LABELS } from "@crewledger/shared/schemas";
import { useEquipmentPermissions } from "./use-equipment-permissions";

interface EquipmentFormPageProps {
  mode: "create" | "edit";
}

export function EquipmentFormPage({ mode }: EquipmentFormPageProps) {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const { canWrite, canViewCosts } = useEquipmentPermissions();
  const [options, setOptions] = useState<EquipmentFormOptions | null>(null);
  const [loading, setLoading] = useState(mode === "edit");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<CreateEquipmentInput>({
    resolver: zodResolver(createEquipmentSchema),
    defaultValues: {
      equipmentId: "",
      name: "",
      type: "OTHER",
      hourlyCost: null,
      siteId: null,
      requiredCertification: null,
      status: "AVAILABLE",
      notes: "",
    },
  });

  useEffect(() => {
    fetchEquipmentOptions()
      .then((res) => { setOptions(res.data); })
      .catch(() => { setOptions(null); });
  }, []);

  useEffect(() => {
    if (mode !== "edit" || !equipmentId) return;
    setLoading(true);
    fetchEquipmentItem(equipmentId)
      .then((res) => {
        const e = res.data;
        form.reset({
          equipmentId: e.equipmentId,
          name: e.name,
          type: e.type,
          hourlyCost: e.hourlyCost,
          siteId: e.siteId,
          requiredCertification: e.requiredCertification,
          status: e.status,
          notes: e.notes ?? "",
        });
      })
      .catch((err: unknown) =>
        { setSubmitError(err instanceof Error ? err.message : "Failed to load equipment"); },
      )
      .finally(() => { setLoading(false); });
  }, [mode, equipmentId, form]);

  if (!canWrite) {
    return (
      <PageHeader title="Access denied" description="You do not have permission to edit equipment." />
    );
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    const payload = {
      ...values,
      hourlyCost: values.hourlyCost ?? null,
      siteId: values.siteId || null,
      requiredCertification: values.requiredCertification || null,
      notes: values.notes?.trim() ? values.notes.trim() : null,
    };
    try {
      if (mode === "create") {
        const res = await createEquipment(payload);
        navigate(`/equipment/${res.data.id}`);
      } else if (equipmentId) {
        const res = await updateEquipment(equipmentId, payload);
        navigate(`/equipment/${res.data.id}`);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save equipment");
    }
  });

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        title={mode === "create" ? "New equipment" : "Edit equipment"}
        description="Register assets, rentals, and tools with hourly cost and site assignment."
      />

      {loading ? (
        <div className="h-96 animate-pulse rounded-xl border bg-card-white" />
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-6 rounded-xl border bg-card-white p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="equipmentId">Equipment ID</Label>
              <Input id="equipmentId" {...form.register("equipmentId")} className="font-mono uppercase" />
              {form.formState.errors.equipmentId ? (
                <p className="text-xs text-red-danger">{form.formState.errors.equipmentId.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <select
                id="type"
                {...form.register("type")}
                className="flex h-9 w-full rounded-md border border-input px-3 text-sm"
              >
                {Object.entries(EQUIPMENT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" {...form.register("name")} placeholder="CAT 320 Excavator" />
            </div>
            {canViewCosts ? (
              <div className="space-y-2">
                <Label htmlFor="hourlyCost">Hourly cost ($)</Label>
                <Input
                  id="hourlyCost"
                  type="number"
                  step="0.01"
                  {...form.register("hourlyCost", { valueAsNumber: true })}
                />
              </div>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                {...form.register("status")}
                className="flex h-9 w-full rounded-md border border-input px-3 text-sm"
              >
                {Object.entries(EQUIPMENT_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="siteId">Assigned site</Label>
              <select
                id="siteId"
                value={form.watch("siteId") ?? ""}
                onChange={(e) => { form.setValue("siteId", e.target.value || null); }}
                className="flex h-9 w-full rounded-md border border-input px-3 text-sm"
              >
                <option value="">Unassigned</option>
                {options?.sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} — {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="requiredCertification">Required certification</Label>
              <select
                id="requiredCertification"
                value={form.watch("requiredCertification") ?? ""}
                onChange={(e) =>
                  { form.setValue(
                    "requiredCertification",
                    e.target.value ? (e.target.value as CreateEquipmentInput["requiredCertification"]) : null,
                  ); }
                }
                className="flex h-9 w-full rounded-md border border-input px-3 text-sm"
              >
                <option value="">None required</option>
                {Object.entries(CERTIFICATION_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="notes">Notes / maintenance notes</Label>
              <textarea
                id="notes"
                {...form.register("notes")}
                rows={4}
                className="w-full rounded-md border border-input px-3 py-2 text-sm"
                placeholder="Maintenance schedule, rental contract #, etc."
              />
            </div>
          </div>

          {submitError ? <p className="text-sm text-red-danger">{submitError}</p> : null}

          <div className="flex gap-2">
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting
                ? "Saving…"
                : mode === "create"
                  ? "Create equipment"
                  : "Save changes"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate(mode === "edit" && equipmentId ? `/equipment/${equipmentId}` : "/equipment")
              }
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export function EquipmentCreatePage() {
  return <EquipmentFormPage mode="create" />;
}

export function EquipmentEditPage() {
  return <EquipmentFormPage mode="edit" />;
}
